import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";

interface User {
  id: string;
  email: string;
}

interface ApiError {
  message: string;
}

const ImplementationStatusManager: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | undefined>();
  const [implementationStatus, setImplementationStatus] = useState<
    boolean | undefined
  >();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await ApiService.request<{ users: User[] }>(
        EndpointKeys.FETCH_ALL_USERS,
      );
      setUsers(response.users);
    } catch (err) {
      const error = err as ApiError;
      setError(`Error fetching users: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const updateImplementationStatus = async (): Promise<void> => {
    if (!selectedUser || implementationStatus === undefined) return;

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      await ApiService.request(
        EndpointKeys.UPDATE_SUBSCRIPTION_IMPLEMENTATION_STATUS,
        {
          method: "POST",
          body: {
            userId: selectedUser,
            inImplementationStatus: implementationStatus,
          },
        },
      );

      setSuccess(true);
    } catch (err) {
      const error = err as ApiError;
      setError(`Error updating status: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUserChange = (value: string): void => {
    setSelectedUser(value);
  };

  const handleStatusChange = (value: string): void => {
    setImplementationStatus(value === "true");
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>Implementation Status Manager</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="bg-green-50 text-green-800 border-green-200">
            <AlertDescription>
              Implementation status updated successfully!
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Select User
            </label>
            <Select onValueChange={handleUserChange} disabled={loading}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a user..." />
              </SelectTrigger>
              <SelectContent>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Implementation Status
            </label>
            <Select
              onValueChange={handleStatusChange}
              disabled={!selectedUser || loading}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select status..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">In Implementation</SelectItem>
                <SelectItem value="false">Not In Implementation</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={updateImplementationStatus}
            disabled={
              !selectedUser || implementationStatus === undefined || loading
            }
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              "Update Implementation Status"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ImplementationStatusManager;
